import { Box, CircularProgress, HairlineCard, Stack, Typography } from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { useOptionalScope } from '../providers/ScopeProvider';
import { useAuth } from '../providers/AuthProvider';
import { useOperationsStatus } from '../lib/operations-api';
import { homeDestination } from '../lib/home-destination';
import { Dashboard } from './Dashboard';

/**
 * `/` — the dashboard for anyone doing casework here, and otherwise the first
 * place the account can actually work (see `homeDestination`). Never a
 * dashboard whose every panel the API refuses.
 */
export function Home() {
  const { t } = useTranslation();
  const scope = useOptionalScope();
  const { user } = useAuth();
  const operations = useOperationsStatus(scope ? (user?.id ?? null) : null);
  const destination = homeDestination({
    can: scope ? scope.can : null,
    scopeLoading: scope?.isLoading ?? false,
    operations: operations.data,
    operationsLoading: Boolean(user?.id) && operations.isLoading,
  });

  switch (destination.kind) {
    case 'dashboard':
      return <Dashboard />;
    case 'redirect':
      return <Navigate to={destination.to} replace />;
    case 'wait':
      return (
        <AppShell>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress aria-label={t('gov.home.loading')} />
          </Box>
        </AppShell>
      );
    default:
      return (
        <AppShell>
          <HairlineCard sx={{ p: 3 }}>
            <Stack spacing={1}>
              <Typography variant="h2" sx={{ fontSize: 20 }}>
                {t('gov.home.nothingTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('gov.home.nothingBody')}
              </Typography>
            </Stack>
          </HairlineCard>
        </AppShell>
      );
  }
}
